import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type TaxIdFieldConfig } from '../model';

import { PrimaryButton } from './primary-button';
import { dynamicViewportHeightStyles } from './reset.styles';
import { rubikTypographyStyles } from './typography.styles';

type TaxIdHelpSheetProps = {
  dataUi?: string;
  isOpen: boolean;
  onClose: () => void;
  taxIdField: TaxIdFieldConfig;
  zIndex: number;
};

const TaxIdHelpBackdrop = styled.div.attrs<{ $dataUi: string }>(
  ({ $dataUi }) => ({
    'data-ui': $dataUi,
  }),
)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  ${dynamicViewportHeightStyles};
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surface.backdrop};
  z-index: ${({ $zIndex }) => $zIndex};
`;

const ConfirmButton = styled(PrimaryButton)`
  margin: 0 auto;
`;

const TaxIdHelpSheetContainer = styled.section`
  width: min(100%, ${({ theme }) => `${theme.frame.width}px`});
  margin-top: auto;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.surface.base};
  border-top-left-radius: ${({ theme }) => theme.scale.px(21)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(21)};
  padding-top: ${({ theme }) => theme.scale.px(38)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(31)}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(28)}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(31)}, env(safe-area-inset-left))`};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(18)};

  & > ${ConfirmButton} {
    margin-top: ${({ theme }) => theme.scale.px(18)};
  }
`;

const TaxIdHelpIntroText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const TaxIdHelpTitle = styled.h2`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
`;

const TaxIdHelpStepsList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(11)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const TaxIdHelpStepText = styled.p`
  margin: 0;
  white-space: pre-line;
`;

const ExternalLink = styled.a`
  color: inherit;
`;

export function TaxIdHelpSheet({
  dataUi = 'tax-id-help-sheet',
  isOpen,
  onClose,
  taxIdField,
  zIndex,
}: TaxIdHelpSheetProps) {
  const { t } = useTranslation();
  const helpKeyPrefix = taxIdField.helpTranslationKeyPrefix;

  if (!isOpen || !helpKeyPrefix || !taxIdField.tipHref) {
    return null;
  }

  return (
    <TaxIdHelpBackdrop $dataUi={dataUi} $zIndex={zIndex}>
      <TaxIdHelpSheetContainer>
        <TaxIdHelpIntroText>{t(`${helpKeyPrefix}.intro`)}</TaxIdHelpIntroText>
        <TaxIdHelpTitle>{t(`${helpKeyPrefix}.title`)}</TaxIdHelpTitle>

        <TaxIdHelpStepsList>
          <TaxIdHelpStepText>{t(`${helpKeyPrefix}.step1`)}</TaxIdHelpStepText>
          <TaxIdHelpStepText>
            {t(`${helpKeyPrefix}.step2Prefix`)}
            <ExternalLink
              href={taxIdField.tipHref}
              rel="noreferrer"
              target="_blank"
            >
              {t(`${helpKeyPrefix}.step2LinkLabel`)}
            </ExternalLink>
            {t(`${helpKeyPrefix}.step2Suffix`)}
          </TaxIdHelpStepText>
          <TaxIdHelpStepText>{t(`${helpKeyPrefix}.step3`)}</TaxIdHelpStepText>
          <TaxIdHelpStepText>{t(`${helpKeyPrefix}.step4`)}</TaxIdHelpStepText>
        </TaxIdHelpStepsList>

        <ConfirmButton type="button" onClick={onClose}>
          {t(`${helpKeyPrefix}.cta`)}
        </ConfirmButton>
      </TaxIdHelpSheetContainer>
    </TaxIdHelpBackdrop>
  );
}
